<template>
  <CupertinoAlertDialog :title="title" :close="onClickOutSide" :width="500">
    <template #header>
      <div class="description">{{ description }}</div>
      <div class="w-full flex flex-center flex-spacebetween box-border px-1 py-1">
        <div>
          <span class="font-bold font-large mr-small">{{ $t('contestEntryDataForm.dialog.selectedCount', { count: selected.length }) }}</span>
          <span>{{ $t('contestEntryDataForm.dialog.selecting') }}</span>
        </div>
        <div>
          <button class="button cancel font-bold mr-1" @click="onClickDeselectAll">{{ $t('contestEntryDataForm.dialog.deselectAll') }}</button>
          <button class="button primary font-bold" @click="onClickSelectAll">{{ $t('contestEntryDataForm.dialog.selectAll') }}</button>
        </div>
      </div>
    </template>
    <template #content>
      <template v-for="item in items">
        <div class="item hover" :key="item[itemsKeyAttribute]" @click="onClickItem(item)">
          <div class="checkbox" :class="{ checked: isChecked(item) }">{{ selectedOrder(item) }}</div>
          <template v-if="type === 'characters'">
            <CharacterItem :novelId="novelId" :character="item" />
          </template>
          <template v-if="type === 'manuscripts'">
            <span class="font-bold">{{ item.title || $t('common.untitled') }}</span>
          </template>
        </div>
      </template>
    </template>
    <template #actions>
      <div class="flex flex-row flex-center flex-spacearound">
        <div class="w-full flex flex-center pointer py-1 hover bottom-radius-10 font-bold" @click="onClickClose">
          <span>{{ close || $t('common.close') }}</span>
        </div>
      </div>
    </template>
  </CupertinoAlertDialog>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import CupertinoAlertDialog from "@/components/ui/dialogs/CupertinoAlertDialog.vue";
import CharacterItem from "@/components/atoms/CharacterItem.vue";
import deepEqual from "deep-equal";

interface Props {
  type?: "characters" | "manuscripts";
  title: string;
  description: string;
  items: any[];
  itemsKeyAttribute: string;
  close?: string;
  novelId: string;
  initialSelected?: any[];
}

interface Data {
  selected: any[];
}

interface Computed {
  isChecked: (item: any) => boolean;
  selectedOrder: (item: any) => number | string;
}

interface Methods {
  onClickOutSide: () => void;
  onClickClose: () => void;
  onClickItem: (item: any) => void;
  onClickDeselectAll: () => void;
  onClickSelectAll: () => void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { CupertinoAlertDialog, CharacterItem },

  props: {
    type: String as PropType<"characters" | "manuscripts">,
    title: String,
    description: String,
    items: Array as PropType<any[]>,
    itemsKeyAttribute: {
      type: String,
      default: "id",
      required: false,
    },
    close: {
      type: String,
      required: false,
    },
    novelId: String as PropType<string>,
    initialSelected: Array as PropType<any[]>,
  },

  data() {
    return {
      selected: [],
    };
  },

  created() {
    const { initialSelected } = this;
    if (initialSelected) {
      this.selected = [...initialSelected];
    }
  },

  computed: {
    isChecked() {
      return (item) => {
        const { selected } = this;
        return selected.some((i) => deepEqual(i, item));
      };
    },
    selectedOrder() {
      return (item) => {
        const { selected } = this;
        const index = selected.findIndex((i) => deepEqual(i, item));
        if (index === -1) {
          return "";
        }
        return index + 1;
      };
    },
  },

  methods: {
    onClickOutSide() {
      this.$close(false);
    },
    onClickClose() {
      this.$close(this.selected);
    },
    onClickItem(item) {
      const { selected, itemsKeyAttribute } = this;
      const index = selected.findIndex((i) => i[itemsKeyAttribute] === item[itemsKeyAttribute]);
      if (index === -1) {
        selected.push(item);
      } else {
        selected.splice(index, 1);
      }
    },
    onClickDeselectAll() {
      this.selected = [];
    },
    onClickSelectAll() {
      this.selected = [...this.items];
    },
  },
});

export type ContestEntryDataDialogProps = Props;
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

// container

.w-full {
  width: 100%;
}

.item {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 15px 20px;

  .checkbox {
    width: 24px;
    height: 24px;
    color: var(--color-text-on-fixed-bg);
    font-weight: var(--font-weight-bold);
    border: 1px solid #669cd9;
    border-radius: var(--radius-xs);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 20px;

    &.checked {
      background-color: #669cd9;
    }
  }
}

.box-border {
  box-sizing: border-box;
}

// button

.button {
  box-sizing: border-box;
  margin: 0;
  padding: 8px 10px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &.primary {
    border: 1px solid #669cd9;
    border-radius: var(--radius-xs);
    background-color: #669cd9;
    color: var(--color-text-on-fixed-bg);
  }

  &.cancel {
    border: none;
    color: inherit;
    background-color: transparent;
  }
}

.pointer {
  cursor: pointer;
}

.hover {
  position: relative;
  @include hover-overlay;
}

// flex

.flex {
  display: flex;
}

.flex-row {
  flex-direction: row;
}

.flex-center {
  align-items: center;
  justify-content: center;
}

.flex-spacearound {
  justify-content: space-around;
}

.flex-spacebetween {
  justify-content: space-between;
}

// padding, margin

.px-1 {
  padding-right: 1rem;
  padding-left: 1rem;
}

.py-1 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.mr-1 {
  margin-right: 1rem;
}

.mr-small {
  margin-right: 0.5rem;
}

// border

.bottom-radius-10 {
  border-radius: 0px 0px var(--radius-sm) var(--radius-sm);
}

// font

.font-bold {
  font-weight: var(--font-weight-bold);
}

.font-large {
  font-size: var(--font-size-md);
}
</style>
